/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件詳細の見出し（案件名）をその場で書き換え、母票の Backlog 件名と案件 Slack チャンネル名をまとめて変える
 * @graph-connects useRenameProject [calls] 案件名の変更
 */
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProjectRenameResponse } from '@cortex/domain-project-intake-contract';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { useRenameProject } from '../../../hooks/useProjects.js';

/**
 * Slack 側で変えられなかったときの理由。変えられた・既に同じなら null（見出しが変われば分かる）。
 * @graph-connects none
 */
export function slackNotice(result: ProjectRenameResponse | undefined): string | null {
  if (result === undefined) return null;
  if (result.slack.status === 'renamed' || result.slack.status === 'unchanged') return null;
  return result.slack.message;
}

/**
 * 案件名の見出しと、その場での変更フォーム。権限・文字数の判定は API に任せ、
 * 返ってきたメッセージをそのまま出す。
 *
 * 見出しは変更が成功したら API が返した名前を優先する。`summary` は BQ の件名で、Backlog の
 * 変更が載るまでの再取得では旧名が返るため、それで見出しを戻さない。
 * 同じ名前のままでも保存できる（Slack だけ失敗したときのやり直し。API は Backlog を「変更なし」で通す）。
 * @graph-connects useRenameProject [calls] 案件名の変更
 */
export function ProjectNameEditor({
  pjKey,
  summary,
}: {
  pjKey: string;
  summary: string;
}): ReactNode {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(summary);
  const mutation = useRenameProject(pjKey);
  // 直近の成功結果は mutation とは別に持つ（編集を開き直すときに前回のエラーだけを消すため）。
  const [lastResult, setLastResult] = useState<ProjectRenameResponse | undefined>(undefined);
  const notice = slackNotice(lastResult);
  const displayed = lastResult?.summary ?? summary;

  if (!isEditing) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-xl font-bold text-[var(--color-text-primary)]">{displayed}</h1>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setDraft(displayed);
            mutation.reset();
            setIsEditing(true);
          }}
        >
          {t('projectDetail.rename.edit')}
        </Button>
        {notice !== null && (
          <span role="status" className="text-xs text-[var(--color-warning)]">
            {t('projectDetail.rename.slackNotRenamed', { reason: notice })}
          </span>
        )}
      </div>
    );
  }

  return (
    <form
      className="flex flex-wrap items-center gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate(draft, {
          onSuccess: (result) => {
            setLastResult(result);
            setIsEditing(false);
          },
        });
      }}
    >
      <Input
        aria-label={t('projectDetail.rename.label')}
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
        }}
        className="min-w-0 flex-1 text-base"
      />
      <Button size="sm" type="submit" disabled={mutation.isPending}>
        {mutation.isPending ? t('projectDetail.rename.saving') : t('projectDetail.rename.save')}
      </Button>
      <Button
        size="sm"
        variant="secondary"
        disabled={mutation.isPending}
        onClick={() => {
          setIsEditing(false);
        }}
      >
        {t('projectDetail.rename.cancel')}
      </Button>
      {mutation.isError && (
        <span role="alert" className="w-full text-xs text-[var(--color-error)]">
          {mutation.error instanceof Error
            ? mutation.error.message
            : t('projectDetail.rename.saveFailed')}
        </span>
      )}
    </form>
  );
}
